import { useEffect, useState, useMemo } from 'react';
import { db } from '../../firebase/config';
import { client } from '../../hooks/Client';
import ScaleMomentVisualisation from '../Visualisations/moment/ScaleMomentVisualisation';
import ScaleDevelopmentBarChart from '../Visualisations/development/ScaleDevelopmentBarChart';
import ScaleDevelopmentLineChart from '../Visualisations/development/ScaleDevelopmentLineChart';
import ScaleDevelopmentPieChart from '../Visualisations/development/ScaleDevelopmentPieChart';
import defaultDates from '../../utils/defaultDates';
import useHierarchyData from '../../hooks/useHierarchyData';
import toMillis from '../../helpers/dataHelpers/matrixDataHelpers/toMillis';
import getMomentTitlesByPosition from '../../helpers/dataHelpers/matrixDataHelpers/getMomentTitlesByPosition';
import formatMomentLabel from '../../helpers/formatMomentLabel';
import { averagesTable } from '../../helpers/dataHelpers/chartDataTable';
import ChartDataTable from './ChartDataTable';
import momentTitlesForRecords from '../../helpers/dataHelpers/momentTitlesForRecords';
import useChartColors from '../../hooks/useChartColors';
import ScaleMetric from './ScaleMetric';
import AnalyticsEmptyState from '../dashboard/AnalyticsEmptyState';
import spinner from '../../images/spinner-ripple.svg';

// AI-CONTEXT: See docs/features/api-data-fetching.md

const ScaleData = ({ field, researchId, startDate, endDate, graphType, graphColor, personaId, filterByResearchId, primaryColorOverride, onTotalResponsesChange, onProjectBreakdownChange }) => {
  const [scaleEnd, setScaleEnd] = useState(0);
  const [pos2title, setPos2title] = useState({});

  // palette is de huisstijl binnen een live rapportage en het vaste Deccos-palet
  // daarbuiten. Deze component zit niet in de colorPalette-propketen — hij haalde
  // zijn kleur zelf op, en werd daardoor bij de omzetting gemist.
  // Zie docs/patterns.md#ui-chart-palette.
  const { palette: colors } = useChartColors();

  const researchIds = useMemo(
    () => (Array.isArray(researchId) ? researchId.filter(Boolean) : researchId ? [researchId] : []),
    [researchId]
  );

  const { defaultStartDate: dsRaw, defaultEndDate: deRaw } = useMemo(() => defaultDates(), []);
  const dsMs = useMemo(() => toMillis(dsRaw) ?? 0, [dsRaw]);
  const deMs = useMemo(() => toMillis(deRaw) ?? 8640000000000000, [deRaw]);
  const startMs = useMemo(() => toMillis(startDate) ?? dsMs, [startDate, dsMs]);
  const endMs = useMemo(() => toMillis(endDate) ?? deMs, [endDate, deMs]);

  // Fetch field metadata (scaleEnd)
  useEffect(() => {
    if (!field) return;
    db.collection('QuestionnaireFields')
      .where('CompagnyID', '==', client)
      .where('ID', '==', field)
      .limit(1)
      .get()
      .then(snap => setScaleEnd(snap.docs[0]?.data()?.ReachEnd || 0));
  }, [field]);

  // Fetch measure-moment titles so x-axis labels read "Title (position)"
  useEffect(() => {
    let cancelled = false;
    getMomentTitlesByPosition(researchIds).then(map => {
      if (!cancelled) setPos2title(map);
    });
    return () => { cancelled = true; };
  }, [JSON.stringify(researchIds)]);

  // Fetch records via Cloud Function
  const { records, hierarchy: projectHierarchy, totalRespondents, loading, error } = useHierarchyData({
    syncType: 'Question',
    id: field,
    startDate: startMs,
    endDate: endMs,
    researchIds,
    personaId,
    filterByResearchId,
  });

  // Process records into chart data
  const { groupedData, projectAverages, totalResponses } = useMemo(() => {
    if (loading || !records.length) return { groupedData: [], projectAverages: {}, totalResponses: 0 };

    const scoresByProject = records.reduce((acc, r) => {
      const name = r._projectName || 'Onbekend';
      (acc[name] ||= []).push(Number(r.Input) || 0);
      return acc;
    }, {});

    const projectAverages = Object.fromEntries(
      Object.entries(scoresByProject).map(([name, inputs]) => {
        const valid = inputs.filter(v => !isNaN(v));
        const avg = valid.length ? valid.reduce((a, b) => a + b, 0) / valid.length : 0;
        return [name, avg.toFixed(1)];
      })
    );

    const byPosition = records.reduce((acc, r) => {
      if (r.MomentPosition != null) {
        (acc[r.MomentPosition] ||= []).push(Number(r.Input) || 0);
      }
      return acc;
    }, {});

    // Meerdere onderzoeken → alleen posities; zie momentTitlesForRecords.
    const titles = momentTitlesForRecords(pos2title, records);
    const sortedPositions = Object.keys(byPosition).map(Number).sort((a, b) => a - b);
    const groupedData = sortedPositions.map((pos) => {
      const arr = byPosition[pos];
      const avg = arr.length ? arr.reduce((sum, v) => sum + v, 0) / arr.length : 0;
      // count: antwoorden op dit meetmoment, voor de cijfertabel op het PDF-vel.
      return { moment: formatMomentLabel(titles[pos], pos), average: avg.toFixed(2), count: arr.length };
    });

    return { groupedData, projectAverages, totalResponses: totalRespondents };
  }, [records, loading, totalRespondents, pos2title]);

  const data = { groupedData, projectAverages, totalResponses };
  const isSingle = groupedData.length < 2;

  useEffect(() => {
    onTotalResponsesChange?.(totalResponses);
  }, [totalResponses, onTotalResponsesChange]);

  useEffect(() => {
    onProjectBreakdownChange?.(projectAverages);
  }, [projectAverages, onProjectBreakdownChange]);
  const mode = isSingle ? 'single' : 'multiple';

  const graphComponents = {
    single: {
      line: <ScaleMomentVisualisation data={data} field={field} projectHierarchy={projectHierarchy} projectAverages={projectAverages} primaryColorOverride={primaryColorOverride} graphColor={graphColor} />,
      bar: <ScaleMomentVisualisation data={data} field={field} projectHierarchy={projectHierarchy} projectAverages={projectAverages} primaryColorOverride={primaryColorOverride} graphColor={graphColor} />,
      pie: <ScaleMomentVisualisation data={data} field={field} projectHierarchy={projectHierarchy} projectAverages={projectAverages} primaryColorOverride={primaryColorOverride} graphColor={graphColor} />,
    },
    multiple: {
      line: <ScaleDevelopmentLineChart groupedData={groupedData} color={primaryColorOverride || colors[1]} totalResponses={totalResponses} scaleEnd={scaleEnd} projectHierarchy={projectHierarchy} projectAverages={projectAverages} graphColor={graphColor} />,
      bar: <ScaleDevelopmentBarChart groupedData={groupedData} colors={colors} primaryColor={primaryColorOverride} totalResponses={totalResponses} scaleEnd={scaleEnd} projectHierarchy={projectHierarchy} projectAverages={projectAverages} graphColor={graphColor} />,
      pie: <ScaleDevelopmentPieChart groupedData={groupedData} colors={colors} projectAverages={projectAverages} totalResponses={totalResponses} scaleEnd={scaleEnd} projectHierarchy={projectHierarchy} graphColor={graphColor} />,
    },
  };

  // Een mislukte read is geen lege vraag: zonder deze tak stond er 'Nog geen
  // gegevens' terwijl de antwoorden er waren (docs/features/public-livereport-data.md).
  // AI-CONTEXT: See docs/pitfalls.md#api-fallback-collapses-failure-into-empty
  if (error) return <p className="graph-data-error">De gegevens konden niet worden geladen.</p>;

  // Laden is geen lege vraag: met een lege lijst tekende de grafiek 'Geen
  // gegevens in deze periode' terwijl de antwoorden onderweg waren (testronde
  // 2026-10-28, B4). AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
  if (loading) return <img src={spinner} alt="loading" />;

  // 'Getal': het gemiddelde als groot getal (testronde 2026-10-05).
  if (graphType === 'metric') {
    if (groupedData.length === 0) return <AnalyticsEmptyState />;
    return <ScaleMetric groupedData={groupedData} totalResponses={totalResponses} />;
  }

  const effectiveGraphType = graphType || (mode === 'multiple' ? 'bar' : 'line');

  return (
    <>
      {graphComponents[mode][effectiveGraphType] || null}
      <ChartDataTable table={averagesTable(groupedData)} />
    </>
  );
};

export default ScaleData;
